import { KaizenThemeProvider } from "@platform/design-system";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router";

import { GoldenClientProvider } from "./api/GoldenClientProvider";
import { CallbackPage } from "./auth/CallbackPage";
import { CasePage } from "./case/CasePage";
import { TestGroupsPage } from "./groups/TestGroupsPage";
import { RumiTesterGate } from "./auth/RumiTesterGate";
import { SessionProvider } from "./auth/SessionProvider";
import { isAuth0Callback } from "./auth/sso";
import { CasesPage } from "./cases/CasesPage";
import { AppLayout } from "./layout/AppLayout";
import { AppNavigationProvider } from "./layout/AppNavigationProvider";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ReviewQueuePage } from "./review/ReviewQueuePage";
import { RunPage } from "./run/RunPage";
import { SuitePage } from "./suites/SuitePage";

import type { ReactNode } from "react";

/** The routes, behind sign-in and the rumi-tester gate; the paths match the old golden UI's. */
export const AppRoutes = (): ReactNode => {
  const { search } = useLocation();
  if (isAuth0Callback(search)) return <CallbackPage />;

  return (
    <Routes>
      <Route
        element={
          <RumiTesterGate>
            <GoldenClientProvider>
              <AppLayout />
            </GoldenClientProvider>
          </RumiTesterGate>
        }
      >
        <Route index element={<CasesPage />} />
        <Route path="cases/:caseId" element={<CasePage />} />
        <Route path="cases/:caseId/runs/:runId" element={<RunPage />} />
        <Route path="review" element={<ReviewQueuePage />} />
        <Route path="groups" element={<TestGroupsPage />} />
        <Route path="suites/:suiteId" element={<SuitePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

export const App = (): ReactNode => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <KaizenThemeProvider>
      <BrowserRouter>
        <AppNavigationProvider>
          <SessionProvider>
            <QueryClientProvider client={queryClient}>
              <AppRoutes />
            </QueryClientProvider>
          </SessionProvider>
        </AppNavigationProvider>
      </BrowserRouter>
    </KaizenThemeProvider>
  );
};
